import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, curriculaAPI, assignmentsAPI, trainingNodesAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users as UsersIcon, 
  Search,
  Edit2,
  Trash2,
  UserPlus,
  Shield,
  Mail,
  Briefcase,
  GraduationCap,
  CheckCircle,
  ClipboardCheck,
  Eye,
  BookOpen,
  FileText,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';

const JOB_ROLES = [
  "Clin Ops",
  "Reg Ops", 
  "Quality Ops",
  "Recr Ops",
  "eSource Ops",
  "Data Ops",
  "Budget Ops"
];

const Users = () => {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [onboardingItems, setOnboardingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Edit user dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', job_roles: [] });
  
  // View Progress dialog
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  
  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    role: 'employee',
    job_roles: []  // Changed from job_role to job_roles array
  });
  const [selectedCurriculumId, setSelectedCurriculumId] = useState('');
  const [selectedTrainingIds, setSelectedTrainingIds] = useState([]);
  const [assignOnboarding, setAssignOnboarding] = useState(true);
  const [createStep, setCreateStep] = useState('user'); // 'user', 'curriculum', 'onboarding', 'trainings'

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [usersRes, curriculaRes, trainingsRes, onboardingRes] = await Promise.all([
        usersAPI.getAll(),
        curriculaAPI.getAll(),
        trainingNodesAPI.getAll(),
        api.get('/onboarding-items').catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setCurricula(curriculaRes.data);
      setTrainings(trainingsRes.data);
      setOnboardingItems(onboardingRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({ 
      name: user.name, 
      role: user.role, 
      job_roles: user.job_roles || (user.job_role ? [user.job_role] : [])
    });
    setShowEditDialog(true);
  };

  const handleViewProgress = async (user) => {
    setSelectedUser(user);
    setLoadingProgress(true);
    setShowProgressDialog(true);
    
    try {
      const response = await api.get(`/users/${user.id}/training-progress`);
      setProgressData(response.data);
    } catch (error) {
      console.error('Failed to load progress:', error);
      toast.error('Failed to load user progress');
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    
    try {
      await usersAPI.update(selectedUser.id, editForm);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      toast.error('You cannot delete yourself');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateForm({
      name: '',
      email: '',
      role: 'employee',
      job_roles: []  // Changed to array
    });
    setSelectedCurriculumId('');
    setSelectedTrainingIds([]);
    setAssignOnboarding(true);
    setCreateStep('user');
    setShowCreateDialog(true);
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email) {
      toast.error('Please fill in name and email');
      return;
    }

    if (!createForm.job_roles || createForm.job_roles.length === 0) {
      toast.error('Please select at least one job role');
      return;
    }

    try {
      // Create the user (no password - they'll set it on first login)
      const response = await usersAPI.create(createForm);
      const newUserId = response.data.user.id;
      toast.success('User created! They can set their password on first login.');

      // Assign curriculum if selected
      if (selectedCurriculumId) {
        try {
          await assignmentsAPI.create({
            user_id: newUserId,
            curriculum_id: selectedCurriculumId
          });
          toast.success('Curriculum assigned');
        } catch (err) {
          console.error('Failed to assign curriculum:', err);
        }
      }

      // Assign onboarding checklist if enabled
      if (assignOnboarding && onboardingItems.length > 0) {
        try {
          await api.post('/onboarding-assignments', {
            user_id: newUserId,
            item_ids: onboardingItems.map(item => item.id)
          });
          toast.success('Onboarding checklist assigned');
        } catch (err) {
          console.error('Failed to assign onboarding:', err);
        }
      }

      setShowCreateDialog(false);
      loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const toggleTraining = (trainingId) => {
    setSelectedTrainingIds(prev => 
      prev.includes(trainingId) 
        ? prev.filter(id => id !== trainingId)
        : [...prev, trainingId]
    );
  };

  // Auto-select curriculum based on job role
  const handleJobRoleToggle = (jobRole) => {
    const currentRoles = createForm.job_roles || [];
    let newRoles;
    if (currentRoles.includes(jobRole)) {
      newRoles = currentRoles.filter(r => r !== jobRole);
    } else {
      newRoles = [...currentRoles, jobRole];
    }
    setCreateForm({ ...createForm, job_roles: newRoles });
    
    // Find matching curriculum based on first selected role
    if (newRoles.length > 0) {
      const matchingCurriculum = curricula.find(c => c.job_role === newRoles[0]);
      if (matchingCurriculum) {
        setSelectedCurriculumId(matchingCurriculum.id);
      }
    } else {
      setSelectedCurriculumId('');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'lead': return 'bg-purple-100 text-purple-700';
      case 'employee': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Group trainings by category
  const trainingsByCategory = trainings.reduce((acc, t) => {
    const cat = t.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-blue-600" />
            User Management
          </h1>
          <p className="text-slate-600 mt-1">
            Manage users, assign curricula and trainings
          </p>
        </div>
        <Button 
          onClick={handleOpenCreateDialog}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="user-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[150px]" data-testid="role-filter">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="lead">Lead</SelectItem>
            <SelectItem value="employee">Employee</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="border-slate-200 shadow-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">User</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Role</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Job Role</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Joined</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{user.name}</p>
                            {user.id === currentUser.id && (
                              <span className="text-xs text-blue-600">(You)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {(user.job_roles && user.job_roles.length > 0) ? (
                          <div className="flex flex-wrap gap-1">
                            {user.job_roles.map((role, idx) => (
                              <Badge key={idx} variant="outline" className="text-slate-600 text-xs">
                                {role}
                              </Badge>
                            ))}
                          </div>
                        ) : user.job_role ? (
                          <Badge variant="outline" className="text-slate-600">
                            {user.job_role}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewProgress(user)}
                            data-testid={`view-progress-${user.id}`}
                            title="View Training Progress"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEdit(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            disabled={user.id === currentUser.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{users.filter(u => u.role === 'admin').length}</p>
            <p className="text-sm text-slate-500">Admins</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{users.filter(u => u.role === 'lead').length}</p>
            <p className="text-sm text-slate-500">Leads</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{users.filter(u => u.role === 'employee').length}</p>
            <p className="text-sm text-slate-500">Employees</p>
          </CardContent>
        </Card>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={editForm.role}
                onValueChange={(v) => setEditForm({ ...editForm, role: v })}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Job Roles <span className="text-xs text-slate-500 font-normal">- Select all that apply</span></Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2" data-testid="edit-user-job-roles">
                {JOB_ROLES.map(role => (
                  <div 
                    key={role}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      editForm.job_roles?.includes(role) 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => {
                      const currentRoles = editForm.job_roles || [];
                      const newRoles = currentRoles.includes(role)
                        ? currentRoles.filter(r => r !== role)
                        : [...currentRoles, role];
                      setEditForm({ ...editForm, job_roles: newRoles });
                    }}
                  >
                    <Checkbox 
                      id={`edit-job-role-${role}`}
                      checked={editForm.job_roles?.includes(role) || false}
                      onCheckedChange={() => {
                        const currentRoles = editForm.job_roles || [];
                        const newRoles = currentRoles.includes(role)
                          ? currentRoles.filter(r => r !== role)
                          : [...currentRoles, role];
                        setEditForm({ ...editForm, job_roles: newRoles });
                      }}
                    />
                    <label 
                      htmlFor={`edit-job-role-${role}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {role}
                    </label>
                  </div>
                ))}
              </div>
              {editForm.job_roles?.length > 0 && (
                <p className="text-xs text-blue-600">
                  {editForm.job_roles.length} role(s) selected - trainings will be auto-assigned on save
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add New User
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={createStep} onValueChange={setCreateStep} className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="user" className="flex items-center gap-2">
                <UsersIcon className="w-4 h-4" />
                User Info
              </TabsTrigger>
              <TabsTrigger value="curriculum" className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Curriculum
              </TabsTrigger>
              <TabsTrigger value="onboarding" className="flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" />
                Onboarding
              </TabsTrigger>
              <TabsTrigger value="trainings" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Extra
              </TabsTrigger>
            </TabsList>

            {/* User Info Tab */}
            <TabsContent value="user" className="space-y-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>Note:</strong> The user will set their own password when they log in for the first time.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="create-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="create-user-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Roles (Functions) * <span className="text-xs text-slate-500 font-normal">- Select all that apply</span></Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2" data-testid="create-user-job-roles">
                    {JOB_ROLES.map(role => (
                      <div 
                        key={role}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          createForm.job_roles?.includes(role) 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => handleJobRoleToggle(role)}
                      >
                        <Checkbox 
                          id={`job-role-${role}`}
                          checked={createForm.job_roles?.includes(role) || false}
                          onCheckedChange={() => handleJobRoleToggle(role)}
                        />
                        <label 
                          htmlFor={`job-role-${role}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {role}
                        </label>
                      </div>
                    ))}
                  </div>
                  {createForm.job_roles?.length > 0 && (
                    <p className="text-xs text-blue-600">
                      {createForm.job_roles.length} role(s) selected - trainings will be auto-assigned
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>System Role</Label>
                  <Select 
                    value={createForm.role}
                    onValueChange={(v) => setCreateForm({ ...createForm, role: v })}
                  >
                    <SelectTrigger data-testid="create-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Curriculum Tab */}
            <TabsContent value="curriculum" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Assign Curriculum</Label>
                <p className="text-sm text-slate-500 mb-3">
                  Select a curriculum to assign to this user. The curriculum includes all required trainings and SOPs.
                </p>
                <div className="grid gap-2 max-h-64 overflow-y-auto">
                  {curricula.map(curriculum => (
                    <div 
                      key={curriculum.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedCurriculumId === curriculum.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-indigo-300'
                      }`}
                      onClick={() => setSelectedCurriculumId(curriculum.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{curriculum.title}</p>
                          <p className="text-sm text-slate-500">
                            {curriculum.training_node_ids?.length || 0} trainings • {curriculum.sop_ids?.length || 0} SOPs
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{curriculum.job_role}</Badge>
                          {selectedCurriculumId === curriculum.id && (
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {selectedCurriculumId && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Curriculum will be assigned upon user creation
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Onboarding Checklist Tab */}
            <TabsContent value="onboarding" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Onboarding Checklist</Label>
                <p className="text-sm text-slate-500 mb-3">
                  Assign the onboarding checklist to help the new employee complete their setup tasks.
                </p>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        id="assign-onboarding"
                        checked={assignOnboarding}
                        onCheckedChange={setAssignOnboarding}
                      />
                      <label htmlFor="assign-onboarding" className="cursor-pointer">
                        <p className="font-medium text-slate-900">Assign Onboarding Checklist</p>
                        <p className="text-sm text-slate-500">{onboardingItems.length} tasks across Setup, Prepare, Trainings, and Study Assignment</p>
                      </label>
                    </div>
                    {assignOnboarding && (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                </div>

                {assignOnboarding && onboardingItems.length > 0 && (
                  <div className="mt-4 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                    <p className="text-sm font-medium text-slate-700 mb-2">Checklist Preview:</p>
                    {['Setup', 'Prepare', 'Trainings', 'Study Assignment'].map(section => {
                      const sectionItems = onboardingItems.filter(item => item.section === section);
                      if (sectionItems.length === 0) return null;
                      return (
                        <div key={section} className="mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase">{section}</p>
                          <ul className="text-sm text-slate-600 ml-3">
                            {sectionItems.slice(0, 3).map(item => (
                              <li key={item.id} className="list-disc list-inside">{item.title}</li>
                            ))}
                            {sectionItems.length > 3 && (
                              <li className="text-slate-400">...and {sectionItems.length - 3} more</li>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Extra Trainings Tab */}
            <TabsContent value="trainings" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Additional Individual Trainings (Optional)</Label>
                <p className="text-sm text-slate-500 mb-3">
                  Select any additional trainings to assign beyond the curriculum. These are tracked separately.
                </p>
                <div className="max-h-64 overflow-y-auto space-y-4">
                  {Object.entries(trainingsByCategory).map(([category, categoryTrainings]) => (
                    <div key={category}>
                      <p className="font-medium text-slate-700 text-sm mb-2">{category}</p>
                      <div className="grid gap-1 ml-2">
                        {categoryTrainings.map(training => (
                          <div 
                            key={training.id}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded"
                          >
                            <Checkbox 
                              id={training.id}
                              checked={selectedTrainingIds.includes(training.id)}
                              onCheckedChange={() => toggleTraining(training.id)}
                            />
                            <label 
                              htmlFor={training.id}
                              className="text-sm text-slate-700 cursor-pointer flex-1"
                            >
                              {training.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {selectedTrainingIds.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    {selectedTrainingIds.length} additional training(s) selected
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateUser} 
              className="bg-blue-600 hover:bg-blue-700" 
              data-testid="create-user-submit-btn"
              disabled={!createForm.name || !createForm.email || !createForm.job_roles?.length}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Progress Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              Training Progress - {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingProgress ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : progressData ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{progressData.summary?.total_trainings || 0}</p>
                  <p className="text-xs text-slate-500">Total Trainings</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{progressData.summary?.required_trainings || 0}</p>
                  <p className="text-xs text-slate-500">Required</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">{progressData.summary?.completed_trainings || 0}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {progressData.summary?.required_trainings > 0 
                      ? Math.round((progressData.summary?.required_completed / progressData.summary?.required_trainings) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-slate-500">Progress</p>
                </div>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-2 mb-3 p-2 bg-slate-50 rounded-lg">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{progressData.user?.email}</span>
                {progressData.user?.job_roles?.map((role, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">{role}</Badge>
                ))}
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Required Training Completion</span>
                  <span className="font-medium">{progressData.summary?.required_completed || 0}/{progressData.summary?.required_trainings || 0}</span>
                </div>
                <Progress 
                  value={progressData.summary?.required_trainings > 0 
                    ? (progressData.summary?.required_completed / progressData.summary?.required_trainings) * 100 
                    : 0} 
                  className="h-2"
                />
              </div>

              {/* Trainings List */}
              <Tabs defaultValue="required" className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="w-fit flex-wrap">
                  <TabsTrigger value="required">
                    Required ({progressData.training_progress?.filter(t => t.is_required).length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="all">
                    All Trainings ({progressData.training_progress?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="quizzes">
                    Quizzes ({progressData.quiz_attempts?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="certificates">
                    Certificates ({(progressData.certifications?.length || 0) + (progressData.training_certificates?.length || 0)})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="required" className="flex-1 overflow-hidden mt-3">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2 pr-4">
                      {progressData.training_progress?.filter(t => t.is_required).map((training) => (
                        <div 
                          key={training.training_id}
                          className={`p-3 rounded-lg border ${
                            training.completed 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {training.completed ? (
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <Clock className="w-5 h-5 text-slate-400" />
                              )}
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{training.label}</p>
                                <p className="text-xs text-slate-500">{training.category}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              {training.completed ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Completed</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-500 text-xs">Pending</Badge>
                              )}
                              {training.completed_at && (
                                <p className="text-xs text-slate-400 mt-1">
                                  {new Date(training.completed_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {progressData.training_progress?.filter(t => t.is_required).length === 0 && (
                        <p className="text-center text-slate-500 py-8">No required trainings assigned</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="all" className="flex-1 overflow-hidden mt-3">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2 pr-4">
                      {progressData.training_progress?.map((training) => (
                        <div 
                          key={training.training_id}
                          className={`p-3 rounded-lg border ${
                            training.completed 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : training.is_required
                                ? 'bg-orange-50 border-orange-200'
                                : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {training.completed ? (
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <Clock className="w-5 h-5 text-slate-400" />
                              )}
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{training.label}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">{training.category}</span>
                                  {training.is_required && (
                                    <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">Required</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Badge className={training.completed ? "bg-emerald-100 text-emerald-700 text-xs" : "bg-slate-100 text-slate-600 text-xs"}>
                              {training.completed ? 'Completed' : 'Pending'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="quizzes" className="flex-1 overflow-hidden mt-3">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2 pr-4">
                      {progressData.quiz_attempts?.map((attempt, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            attempt.passed 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {attempt.passed ? (
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500" />
                              )}
                              <div>
                                <p className="font-medium text-slate-900 text-sm">Quiz Attempt</p>
                                <p className="text-xs text-slate-500">
                                  {new Date(attempt.timestamp || attempt.completed_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">{attempt.score}%</p>
                              <Badge className={attempt.passed ? "bg-emerald-100 text-emerald-700 text-xs" : "bg-red-100 text-red-700 text-xs"}>
                                {attempt.passed ? 'Passed' : 'Failed'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!progressData.quiz_attempts || progressData.quiz_attempts.length === 0) && (
                        <p className="text-center text-slate-500 py-8">No quiz attempts yet</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="certificates" className="flex-1 overflow-hidden mt-3">
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2 pr-4">
                      {/* External Certifications (GCP, HIPAA, IATA) */}
                      {progressData.certifications?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-slate-700 mb-2">External Certifications</h4>
                          {progressData.certifications.map((cert, idx) => (
                            <div 
                              key={idx}
                              className="p-3 rounded-lg border bg-blue-50 border-blue-200 mb-2"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{cert.name}</p>
                                  <p className="text-xs text-slate-500">{cert.issuing_organization}</p>
                                </div>
                                <div className="text-right">
                                  {cert.expiry_date && (
                                    <p className="text-xs text-slate-600">
                                      Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                                    </p>
                                  )}
                                  {cert.document_url && (
                                    <a 
                                      href={cert.document_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      View Certificate
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Training Certificates (uploaded proof) */}
                      {progressData.training_certificates?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 mb-2">Training Certificates</h4>
                          {progressData.training_certificates.map((cert, idx) => (
                            <div 
                              key={idx}
                              className="p-3 rounded-lg border bg-emerald-50 border-emerald-200 mb-2"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{cert.training_name || 'Training Certificate'}</p>
                                  <p className="text-xs text-slate-500">
                                    Signed: {new Date(cert.signature_date || cert.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="text-right">
                                  {cert.document_url && (
                                    <a 
                                      href={cert.document_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      View Certificate
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {(!progressData.certifications?.length && !progressData.training_certificates?.length) && (
                        <p className="text-center text-slate-500 py-8">No certificates uploaded yet</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No data available</p>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowProgressDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
